import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  Grid3X3,
  List,
  Clock,
  ArrowUpDown,
  CheckSquare,
  X,
  Star,
  EyeOff,
  Trash2,
} from 'lucide-react';

import { useUiStore } from '@/store/uiStore';
import type { GalleryViewMode } from '@/store/uiStore';
import { usePhotos, useUpdatePhoto, useBulkAction } from '@/api/hooks/usePhotos';
import type { PhotoFilters as PhotoFiltersType } from '@/api/types/photo.types';
import { PhotoGrid } from '@/components/gallery/PhotoGrid';
import { TimelineView } from '@/components/gallery/TimelineView';
import { PhotoFilters } from '@/components/gallery/PhotoFilters';
import { PhotoDetailPanel } from '@/components/gallery/PhotoDetailPanel';

const viewModes: { mode: GalleryViewMode; icon: React.ReactNode; label: string }[] = [
  { mode: 'grid', icon: <Grid3X3 className="h-4 w-4" />, label: 'Grid' },
  { mode: 'list', icon: <List className="h-4 w-4" />, label: 'List' },
  { mode: 'timeline', icon: <Clock className="h-4 w-4" />, label: 'Timeline' },
];

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: '-created_at', label: 'Newest first' },
  { value: 'created_at', label: 'Oldest first' },
  { value: '-taken_at', label: 'Taken (newest)' },
  { value: 'taken_at', label: 'Taken (oldest)' },
  { value: '-quality_score', label: 'Best quality' },
  { value: 'quality_score', label: 'Worst quality' },
  { value: '-face_count', label: 'Most faces' },
];

export function GalleryPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('event') ?? undefined;

  // UI state
  const galleryViewMode = useUiStore((s) => s.galleryViewMode);
  const setGalleryViewMode = useUiStore((s) => s.setGalleryViewMode);
  const selectedPhotoIds = useUiStore((s) => s.selectedPhotoIds);
  const isSelectionMode = useUiStore((s) => s.isSelectionMode);
  const togglePhotoSelection = useUiStore((s) => s.togglePhotoSelection);
  const deselectAllPhotos = useUiStore((s) => s.deselectAllPhotos);
  const setSelectionMode = useUiStore((s) => s.setSelectionMode);

  // Filters
  const [filters, setFilters] = useState<PhotoFiltersType>({
    event: eventId,
    ordering: '-created_at',
  });

  // Force taken_at sort and larger page size for timeline mode
  const effectiveFilters = useMemo(() => {
    if (galleryViewMode === 'timeline') {
      return { ...filters, ordering: 'taken_at', page_size: 500 };
    }
    return filters;
  }, [filters, galleryViewMode]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Detail panel
  const [detailPhotoId, setDetailPhotoId] = useState<string | null>(null);

  // Sort
  const handleSortChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setFilters((prev) => ({ ...prev, ordering: e.target.value }));
    },
    [],
  );

  // Data
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = usePhotos(effectiveFilters);

  const updateMutation = useUpdatePhoto();
  const bulkMutation = useBulkAction();

  // Flatten all pages into a single array
  const photos = useMemo(
    () => data?.pages.flatMap((page) => page.results) ?? [],
    [data],
  );

  const totalCount = photos.length + (hasNextPage ? 60 : 0);

  // Handlers
  const handlePhotoClick = useCallback((photoId: string) => {
    setDetailPhotoId(photoId);
  }, []);

  const handleToggleStar = useCallback(
    (photoId: string, starred: boolean) => {
      updateMutation.mutate({ photoId, payload: { is_starred: starred } });
    },
    [updateMutation],
  );

  const handleLoadMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  // Detail panel navigation
  const currentIndex = detailPhotoId
    ? photos.findIndex((p) => p.id === detailPhotoId)
    : -1;

  const handlePrevPhoto = currentIndex > 0
    ? () => setDetailPhotoId(photos[currentIndex - 1].id)
    : null;

  const handleNextPhoto =
    currentIndex >= 0 && currentIndex < photos.length - 1
      ? () => setDetailPhotoId(photos[currentIndex + 1].id)
      : null;

  // Bulk actions
  const handleBulkStar = useCallback(() => {
    bulkMutation.mutate({
      action: 'star',
      photo_ids: Array.from(selectedPhotoIds),
    });
    deselectAllPhotos();
  }, [bulkMutation, selectedPhotoIds, deselectAllPhotos]);

  const handleBulkHide = useCallback(() => {
    bulkMutation.mutate({
      action: 'hide',
      photo_ids: Array.from(selectedPhotoIds),
    });
    deselectAllPhotos();
  }, [bulkMutation, selectedPhotoIds, deselectAllPhotos]);

  const handleBulkDelete = useCallback(() => {
    bulkMutation.mutate({
      action: 'delete',
      photo_ids: Array.from(selectedPhotoIds),
    });
    deselectAllPhotos();
  }, [bulkMutation, selectedPhotoIds, deselectAllPhotos]);

  return (
    <div className="page-container">
      {/* Top bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <h1 className="page-title">{t('nav.gallery')}</h1>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {photos.length} {t('gallery.photos')}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Selection mode toggle */}
          <button
            onClick={() => setSelectionMode(!isSelectionMode)}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
              isSelectionMode
                ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
            }`}
          >
            <CheckSquare className="h-4 w-4" />
            {t('gallery.select')}
          </button>

          {/* Sort dropdown */}
          <div className="relative inline-flex items-center">
            <ArrowUpDown className="pointer-events-none absolute left-2.5 h-3.5 w-3.5 text-gray-400" />
            <select
              value={filters.ordering ?? '-created_at'}
              onChange={handleSortChange}
              className="appearance-none rounded-lg border border-gray-200 bg-white py-1.5 pl-8 pr-8 text-xs text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* View mode switcher */}
          <div className="flex rounded-lg border border-gray-200 dark:border-gray-700">
            {viewModes.map(({ mode, icon, label }) => (
              <button
                key={mode}
                onClick={() => setGalleryViewMode(mode)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                  galleryViewMode === mode
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
                title={label}
              >
                {icon}
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {isSelectionMode && selectedPhotoIds.size > 0 && (
        <div className="mt-3 flex items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-2 dark:border-primary-800 dark:bg-primary-900/20">
          <span className="text-sm font-medium text-primary-700 dark:text-primary-400">
            {t('gallery.selected', { count: selectedPhotoIds.size })}
          </span>
          <div className="flex items-center gap-1.5">
            <button
              onClick={handleBulkStar}
              className="inline-flex items-center gap-1 rounded-md bg-white px-2.5 py-1 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300"
            >
              <Star className="h-3.5 w-3.5" />
              {t('gallery.star')}
            </button>
            <button
              onClick={handleBulkHide}
              className="inline-flex items-center gap-1 rounded-md bg-white px-2.5 py-1 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300"
            >
              <EyeOff className="h-3.5 w-3.5" />
              {t('gallery.hide')}
            </button>
            <button
              onClick={handleBulkDelete}
              className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t('actions.delete')}
            </button>
          </div>
          <button
            onClick={deselectAllPhotos}
            className="ml-auto text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="mt-4">
        <PhotoFilters
          filters={filters}
          onChange={setFilters}
          isOpen={filtersOpen}
          onToggle={() => setFiltersOpen((v) => !v)}
        />
      </div>

      {/* Grid */}
      <div className="mt-4">
        {isLoading ? (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {Array.from({ length: 24 }).map((_, i) => (
              <div
                key={i}
                className="aspect-square animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700"
              />
            ))}
          </div>
        ) : photos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('gallery.empty')}
            </p>
          </div>
        ) : galleryViewMode === 'timeline' ? (
          <TimelineView
            photos={photos}
            onPhotoClick={handlePhotoClick}
            eventId={eventId}
          />
        ) : (
          <PhotoGrid
            photos={photos}
            totalCount={totalCount}
            hasNextPage={!!hasNextPage}
            isLoadingMore={isFetchingNextPage}
            onLoadMore={handleLoadMore}
            selectedIds={selectedPhotoIds}
            isSelectionMode={isSelectionMode}
            onSelect={togglePhotoSelection}
            onPhotoClick={handlePhotoClick}
            onToggleStar={handleToggleStar}
          />
        )}
      </div>

      {/* Detail panel */}
      {detailPhotoId && (
        <PhotoDetailPanel
          photoId={detailPhotoId}
          onClose={() => setDetailPhotoId(null)}
          onPrev={handlePrevPhoto}
          onNext={handleNextPhoto}
        />
      )}
    </div>
  );
}
